<template>
  <div>
    <Head :title="`Guía ${shipment.correlative}`" />

    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div class="flex items-center gap-3">
        <Link href="/shipments" class="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors shadow-sm">
          <i class="fa fa-arrow-left" />
        </Link>
        <div>
          <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-3">
            {{ shipment.correlative }}
            <span 
              class="text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider"
              :class="getStatusClass(shipment.status)"
            >
              {{ getStatusLabel(shipment.status) }}
            </span>
          </h1>
          <p class="text-gray-500 text-sm mt-1">Detalles de la guía de despacho y sus órdenes.</p>
        </div>
      </div>
      
      <div class="flex items-center gap-3">
        <!-- Generar PDF -->
        <a 
          :href="`/shipments/${shipment.id}/pdf`" 
          target="_blank"
          class="px-4 py-2 border border-emerald-500 text-emerald-600 hover:bg-emerald-50 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-file-pdf" />
          Imprimir Guía PDF
        </a>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Datos de la Guía (Izquierda) -->
      <div class="lg:col-span-1 space-y-6">
        <!-- Info Principal -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 class="text-lg font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3">Información del Transporte</h2>
          
          <div class="space-y-4">
            <div>
              <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Empresa Transportista</p>
              <p class="text-gray-900 font-bold flex items-center gap-2">
                <i class="fa fa-truck text-orange-400 w-4" />
                {{ shipment.carrier?.name || 'N/A' }}
              </p>
            </div>
            
            <div>
              <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Unidad y Chofer</p>
              <p class="text-gray-800 font-medium flex items-center gap-2">
                <i class="fa fa-car text-gray-400 w-4" />
                {{ shipment.transport_unit?.license_plate }} 
                <span class="text-gray-500 font-normal">({{ shipment.transport_unit?.driver_name || 'Sin chofer' }})</span>
              </p>
            </div>

            <div>
              <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Fecha Programada de Salida</p>
              <p class="text-gray-800 font-medium flex items-center gap-2">
                <i class="fa fa-calendar-alt text-gray-400 w-4" />
                {{ formatDate(shipment.dispatch_date) || 'No asignada' }}
              </p>
            </div>

            <div v-if="shipment.observations">
              <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Observaciones</p>
              <p class="text-gray-600 bg-gray-50 p-3 rounded-lg text-sm border border-gray-100">
                {{ shipment.observations }}
              </p>
            </div>
          </div>
        </div>

        <!-- Acciones Rápidas (Cambio de Estado) -->
        <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-sm p-6">
          <h2 class="text-lg font-bold text-white mb-4">Gestión de Estado</h2>
          <div class="space-y-3">
            <button 
              v-if="shipment.status === 'pending'"
              class="w-full py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors flex justify-center items-center gap-2"
              @click="changeStatus('in_progress')"
            >
              <i class="fa fa-play" /> Iniciar Despacho (En Ruta)
            </button>
            
            <button 
              v-if="shipment.status === 'in_progress'"
              class="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors flex justify-center items-center gap-2"
              @click="changeStatus('delivered')"
            >
              <i class="fa fa-check" /> Marcar como Entregado
            </button>
            
            <p v-if="shipment.status === 'delivered'" class="text-emerald-400 text-sm font-medium flex items-center gap-2 bg-emerald-900/50 p-3 rounded-lg border border-emerald-800/50">
              <i class="fa fa-check-circle" />
              Este despacho ya ha sido completado.
            </p>
          </div>
        </div>
      </div>

      <!-- Órdenes Asociadas (Derecha) -->
      <div class="lg:col-span-2">
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-6 border-b border-gray-100 bg-gray-50/50">
            <h2 class="text-lg font-bold text-gray-800">Órdenes Asignadas ({{ shipment.orders?.length || 0 }})</h2>
          </div>

          <!-- Mobile View -->
          <div class="sm:hidden divide-y divide-gray-100">
            <div v-for="order in shipment.orders" :key="order.id" class="p-4">
              <div class="flex justify-between items-start mb-2">
                <span class="font-bold text-gray-900">{{ order.number_order || `Nº ${order.id}` }}</span>
                <span class="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">{{ order.order_state }}</span>
              </div>
              <p class="text-sm text-gray-600 mb-1"><i class="fa fa-user text-gray-400 mr-2 w-4" />{{ order.client?.name || order.client?.companyName || 'Cliente General' }}</p>
              <p class="text-xs text-gray-500"><i class="fa fa-map-marker-alt text-gray-400 mr-2 w-4" />{{ order.shipping_info || order.client?.address || 'Sin destino' }}</p>
            </div>
            <div v-if="!shipment.orders || shipment.orders.length === 0" class="p-8 text-center text-gray-500">
              No hay órdenes en este despacho.
            </div>
          </div>

          <!-- Desktop View -->
          <div class="hidden sm:block overflow-x-auto">
            <table class="w-full whitespace-nowrap">
              <thead>
                <tr class="bg-white text-left border-b border-gray-100">
                  <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Orden</th>
                  <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Cliente</th>
                  <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Destino</th>
                  <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-center">Estado</th>
                  <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Total</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr 
                  v-for="order in shipment.orders" 
                  :key="order.id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <td class="px-6 py-4 font-bold text-gray-900">
                    {{ order.number_order || `Nº ${order.id}` }}
                  </td>
                  
                  <td class="px-6 py-4 text-gray-600">
                    {{ order.client?.name || order.client?.companyName || 'Cliente General' }}
                  </td>

                  <td class="px-6 py-4 text-gray-500 text-sm max-w-[200px] truncate" :title="order.shipping_info || order.client?.address">
                    {{ order.shipping_info || order.client?.address || 'Sin destino' }}
                  </td>

                  <td class="px-6 py-4 text-center">
                    <span class="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-medium">
                      {{ order.order_state }}
                    </span>
                  </td>

                  <td class="px-6 py-4 text-right font-medium text-gray-900">
                    $ {{ parseFloat(order.total).toFixed(2) }}
                  </td>
                </tr>
                <tr v-if="!shipment.orders || shipment.orders.length === 0">
                  <td colspan="5" class="px-6 py-12 text-center text-gray-500">
                    No hay órdenes asociadas a este despacho.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  name: 'ShipmentsShow',
  
  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    shipment: {
      type: Object,
      required: true,
    },
  },

  methods: {
    getStatusLabel(status) {
      const labels = {
        'pending': 'Por Iniciar',
        'in_progress': 'En Curso',
        'delivered': 'Entregado',
      }
      return labels[status] || status
    },
    
    getStatusClass(status) {
      if (status === 'pending') return 'bg-yellow-100 text-yellow-800 border border-yellow-200'
      if (status === 'in_progress') return 'bg-blue-100 text-blue-800 border border-blue-200'
      if (status === 'delivered') return 'bg-green-100 text-green-800 border border-green-200'
      return 'bg-gray-100 text-gray-800 border border-gray-200'
    },

    formatDate(dateStr) {
      if (!dateStr) return null
      // Truncate to just the date if it's a datetime string to prevent timezone shifts
      const datePart = dateStr.includes(' ') ? dateStr.split(' ')[0] : (dateStr.includes('T') ? dateStr.split('T')[0] : dateStr)
      const parts = datePart.split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}` // DD/MM/YYYY
      }
      return dateStr
    },

    changeStatus(newStatus) {
      if (confirm(`¿Estás seguro de cambiar el estado a ${this.getStatusLabel(newStatus)}?`)) {
        this.$inertia.patch(`/shipments/${this.shipment.id}/status`, { status: newStatus }, {
          preserveScroll: true,
        })
      }
    },
  },
}
</script>
